'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, TextInput, Text, Paper, Stack, ScrollArea, Loader, Group, Alert } from '@mantine/core';
import { IconMapPin, IconSearch, IconCurrentLocation } from '@tabler/icons-react';
import { InPostPaczkomat } from '@/types';
import { useLanguage } from '@/context/LanguageContext';

// Import Leaflet CSS
import 'leaflet/dist/leaflet.css';

// Import Leaflet type
import type L from 'leaflet';

interface InPostSelectorProps {
  onSelect: (paczkomat: InPostPaczkomat) => void;
  selectedPaczkomat?: InPostPaczkomat;
}

export const InPostSelector: React.FC<InPostSelectorProps> = ({
  onSelect,
  selectedPaczkomat
}) => {
  const { t } = useLanguage();
  const [opened, setOpened] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [paczkomats, setPaczkomats] = useState<InPostPaczkomat[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedOnMap, setSelectedOnMap] = useState<any | null>(null);
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState<[number, number]>([52.2297, 21.0122]); // Warsaw, Poland
  
  const defaultCenter: [number, number] = [52.2297, 21.0122]; // Warsaw, Poland
  
  // Refs for imperative Leaflet map management
  const mapRef = useRef<L.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const markersRef = useRef<L.Marker[]>([]);
  const userMarkerRef = useRef<L.Marker | null>(null);
  const markerDataRef = useRef<Map<L.Marker, any>>(new Map()); // Map markers to their paczkomat data

  // Helper function to extract coordinates from paczkomat data
  const getCoordinates = (paczkomat: any): [number, number] | null => {
    let lat: number | null = null;
    let lng: number | null = null;

    // Try different possible structures
    if (paczkomat.location?.latitude && paczkomat.location?.longitude) {
      lat = parseFloat(paczkomat.location.latitude);
      lng = parseFloat(paczkomat.location.longitude);
    } else if (paczkomat.location?.lat && paczkomat.location?.lng) {
      lat = parseFloat(paczkomat.location.lat);
      lng = parseFloat(paczkomat.location.lng);
    } else if (paczkomat.latitude && paczkomat.longitude) {
      lat = parseFloat(paczkomat.latitude);
      lng = parseFloat(paczkomat.longitude);
    } else if (paczkomat.lat && paczkomat.lng) {
      lat = parseFloat(paczkomat.lat);
      lng = parseFloat(paczkomat.lng);
    } else if (paczkomat.coordinates?.latitude && paczkomat.coordinates?.longitude) {
      lat = parseFloat(paczkomat.coordinates.latitude);
      lng = parseFloat(paczkomat.coordinates.longitude);
    } else if (paczkomat.coordinates?.lat && paczkomat.coordinates?.lng) {
      lat = parseFloat(paczkomat.coordinates.lat);
      lng = parseFloat(paczkomat.coordinates.lng);
    } else if (Array.isArray(paczkomat.location)) {
      lat = parseFloat(paczkomat.location[0]);
      lng = parseFloat(paczkomat.location[1]);
    } else if (Array.isArray(paczkomat.coordinates)) {
      lat = parseFloat(paczkomat.coordinates[0]);
      lng = parseFloat(paczkomat.coordinates[1]);
    }

    if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
      return [lat, lng];
    }
    
    return null;
  };

  // Search using the new Geowidget API
  const searchPaczkomats = async (query: string) => {
    const trimmedQuery = query.trim();
    
    if (trimmedQuery.length < 3) {
      return [];
    }
    
    try {
      const apiKey = import.meta.env.VITE_INPOST_API_KEY || '';
      const url = new URL('https://api.inpost-group.com/geowidget/v1/points');
      url.searchParams.set('query', trimmedQuery);
      url.searchParams.set('status', 'Operating,NonOperating,Overloaded');
      url.searchParams.set('functions', 'parcel_collect');
      url.searchParams.set('perPage', '50');
      url.searchParams.set('page', '1');
      url.searchParams.set('type', 'parcel_locker,pop');
      url.searchParams.set('country', 'PL');
      
      // Add API key if available
      const headers: HeadersInit = {};
      if (apiKey) {
        headers['Authorization'] = `Bearer ${apiKey}`;
      }
      
      const response = await fetch(url.toString(), { headers });
      
      if (!response.ok) {
        console.error('Geowidget API error:', response.status, response.statusText);
        return [];
      }
      
      const data = await response.json();
      const results = data.items || data.data || [];
      console.log(`Geowidget API returned ${results.length} results for query: "${trimmedQuery}"`);
      
      // Debug: log first result structure
      if (results.length > 0) {
        console.log('Sample paczkomat structure:', results[0]);
      }
      
      return results;
    } catch (error) {
      console.error('Error searching paczkomats:', error);
      return [];
    }
  };

  const searchByCoordinates = async (lat: number, lng: number): Promise<any[]> => {
    try {
      const apiKey = import.meta.env.VITE_INPOST_API_KEY || '';
      const url = new URL('https://api.inpost-group.com/geowidget/v1/points');
      // Use relative_point format for coordinates
      url.searchParams.set('relative_point', `${lat},${lng}`);
      url.searchParams.set('status', 'Operating,NonOperating,Overloaded');
      url.searchParams.set('functions', 'parcel_collect');
      url.searchParams.set('perPage', '50');
      url.searchParams.set('page', '1');
      url.searchParams.set('type', 'parcel_locker,pop');
      url.searchParams.set('country', 'PL');
      
      const headers: HeadersInit = {};
      if (apiKey) {
        headers['Authorization'] = `Bearer ${apiKey}`;
      }
      
      const response = await fetch(url.toString(), { headers });
      
      if (!response.ok) {
        console.error('Geowidget API error:', response.status, response.statusText);
        return [];
      }
      
      const data = await response.json();
      return data.items || data.data || [];
    } catch (error) {
      console.error('Error searching by coordinates:', error);
      return [];
    }
  };

  // Get user's current location
  const handleFindNearby = () => {
    if (!navigator.geolocation) {
      setError(t('paczkomat.error.geolocationNotSupported'));
      return;
    }

    setLocationLoading(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        setUserLocation([lat, lng]);
        setMapCenter([lat, lng]);
        setLocationLoading(false);

        // Search for nearby paczkomats
        setLoading(true);
        try {
          const results = await searchByCoordinates(lat, lng);
          setPaczkomats(results);
          setSearchQuery(''); // Clear search query
          
          if (results.length === 0) {
            setError(t('paczkomat.error.noNearbyFound'));
          }
        } catch (err) {
          setError(t('paczkomat.error.loadNearbyFailed'));
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        setLocationLoading(false);
        setError(t('paczkomat.error.locationError', { message: err.message }));
      }
    );
  };

  // Initialize Leaflet map ONCE when modal opens
  useEffect(() => {
    // Only initialize if modal is opened and map doesn't exist yet
    if (!opened || mapRef.current) {
      return;
    }

    // Wait for next tick to ensure DOM is ready and ref is attached
    const timer = setTimeout(async () => {
      // Check again inside the timeout - the ref should be attached by now
      if (!mapContainerRef.current || mapRef.current) {
        console.log('Map container not ready or map already exists');
        return;
      }

      try {
        // Dynamically import Leaflet only on client side
        const L = await import('leaflet');
        
        // Fix default marker icon
        delete (L.Icon.Default.prototype as any)._getIconUrl;
        L.Icon.Default.mergeOptions({
          iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
          iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
        });

        // Create the map instance ONCE
        const map = L.map(mapContainerRef.current).setView(mapCenter, 7);
        mapRef.current = map;

        // Add tile layer
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        }).addTo(map);

        console.log('Leaflet map initialized successfully');
      } catch (error) {
        console.error('Error initializing map:', error);
      }
    }, 150);

    return () => {
      clearTimeout(timer);
    };
  }, [opened, mapCenter]); // Include mapCenter to use the correct initial position

  // Cleanup map when modal closes
  useEffect(() => {
    if (!opened && mapRef.current) {
      try {
        mapRef.current.remove();
        mapRef.current = null;
        markersRef.current = [];
        userMarkerRef.current = null;
        console.log('Leaflet map cleaned up');
      } catch (error) {
        console.error('Error cleaning up map:', error);
      }
    }
  }, [opened]);

  // Update map view and markers when data changes
  useEffect(() => {
    if (!mapRef.current || !opened) {
      return;
    }

    const updateMap = async () => {
      const map = mapRef.current;
      if (!map) return;

      try {
        const L = await import('leaflet');

        // Clear all previous markers
        markersRef.current.forEach(marker => marker.remove());
        markersRef.current = [];
        markerDataRef.current.clear();
        
        if (userMarkerRef.current) {
          userMarkerRef.current.remove();
          userMarkerRef.current = null;
        }

        // Create custom icons for selected and unselected markers
        const defaultIcon = L.icon({
          iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
          iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41]
        });

        // Add user location marker if available
        if (userLocation) {
          const marker = L.marker(userLocation).addTo(map);
          marker.bindPopup(t('paczkomat.yourLocation'));
          userMarkerRef.current = marker;
        }

        // Add paczkomat markers
        paczkomats.forEach((paczkomat: any) => {
          const coords = getCoordinates(paczkomat);
          if (!coords) return;

          const name = paczkomat.name || paczkomat.locationName || '';
          const addressLine1 = paczkomat.address?.line1 || paczkomat.address?.street || paczkomat.addressLine1 || paczkomat.address || '';
          const postCode = paczkomat.address_details?.post_code || paczkomat.address?.postCode || paczkomat.postCode || paczkomat.postcode || '';
          const city = paczkomat.address_details?.city || paczkomat.address?.city || paczkomat.city || '';

          const marker = L.marker(coords, { icon: defaultIcon }).addTo(map);
          marker.bindPopup(`<div><strong>${name}</strong><br/>${addressLine1}<br/>${postCode} ${city}</div>`);
          marker.on('click', () => {
            setSelectedOnMap(paczkomat);
          });

          markersRef.current.push(marker);
          markerDataRef.current.set(marker, paczkomat);
        });

        // Update map view
        if (paczkomats.length > 0) {
          const bounds = L.latLngBounds(
            paczkomats
              .map(p => getCoordinates(p))
              .filter((coords): coords is [number, number] => coords !== null)
          );
          
          if (bounds.isValid()) {
            map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
          }
        } else if (userLocation) {
          map.setView(userLocation, 13);
        } else {
          map.setView(mapCenter, 7);
        }
      } catch (error) {
        console.error('Error updating map:', error);
      }
    };

    updateMap();
  }, [paczkomats, userLocation, mapCenter, opened]);

  // Update map center when it changes (without reinitializing)
  useEffect(() => {
    if (mapRef.current && opened && paczkomats.length === 0 && !userLocation) {
      mapRef.current.setView(mapCenter, 7);
    }
  }, [mapCenter, opened, paczkomats.length, userLocation]);

  // Handle selection highlighting: update marker icon and pan to selected
  useEffect(() => {
    if (!mapRef.current || !opened || markersRef.current.length === 0) {
      return;
    }

    const highlightSelected = async () => {
      try {
        const L = await import('leaflet');
        const map = mapRef.current;
        if (!map) return;

        // Create custom icons
        const defaultIcon = L.icon({
          iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
          iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41]
        });

        const selectedIcon = L.icon({
          iconRetinaUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
          iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41]
        });

        // Get selected ID
        const selectedId = selectedOnMap?.name || selectedOnMap?.locationName || selectedOnMap?.id || '';

        // Update all markers
        markersRef.current.forEach((marker) => {
          const paczkomatData = markerDataRef.current.get(marker);
          if (paczkomatData) {
            const paczkomatId = paczkomatData.name || paczkomatData.locationName || paczkomatData.id || '';
            
            if (paczkomatId === selectedId && selectedId) {
              // This is the selected marker - highlight it
              marker.setIcon(selectedIcon);
              marker.openPopup();
              
              // Pan to the selected marker
              const coords = getCoordinates(paczkomatData);
              if (coords) {
                map.setView(coords, Math.max(map.getZoom(), 14), { animate: true });
              }
            } else {
              // Reset to default icon
              marker.setIcon(defaultIcon);
              marker.closePopup();
            }
          }
        });
      } catch (error) {
        console.error('Error highlighting selected marker:', error);
      }
    };

    highlightSelected();
  }, [selectedOnMap, opened]);

  // Execute search when user types
  useEffect(() => {
    if (searchQuery.length < 3) {
      setPaczkomats([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      
      try {
        console.log(`Searching for: "${searchQuery}"`);
        const results = await searchPaczkomats(searchQuery);
        setPaczkomats(results);
        
        if (results.length === 0) {
          setError(t('paczkomat.error.noResultsFound'));
        }
      } catch (err: any) {
        console.error('Search error:', err);
        setError(t('paczkomat.error.searchFailed'));
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSelect = (paczkomat: any) => {
    // Handle both old and new API formats
    const formatted: InPostPaczkomat = {
      location_id: paczkomat.name || paczkomat.locationName || paczkomat.id || '',
      name: paczkomat.name || paczkomat.locationName || '',
      location: {
        latitude: parseFloat(paczkomat.location?.latitude || paczkomat.latitude || paczkomat.location?.lat || '0'),
        longitude: parseFloat(paczkomat.location?.longitude || paczkomat.longitude || paczkomat.location?.lng || '0')
      },
      address: {
        line1: paczkomat.address?.line1 || paczkomat.address?.street || paczkomat.addressLine1 || paczkomat.address || '',
        line2: paczkomat.address?.line2 || paczkomat.addressLine2 || '',
        city: paczkomat.address_details?.city || paczkomat.address?.city || paczkomat.city || '',
        post_code: paczkomat.address_details?.post_code || paczkomat.address?.postCode || paczkomat.postCode || paczkomat.postcode || ''
      },
      opening_hours: paczkomat.opening_hours || paczkomat.openingHours,
      payment_point_descr: paczkomat.payment_point_descr || paczkomat.paymentPointDescr,
      partner_id: paczkomat.partner_id || paczkomat.partnerId,
      type: Array.isArray(paczkomat.type) ? paczkomat.type.join(',') : (paczkomat.type || ''),
      address_details: paczkomat.address_details || {
        city: paczkomat.address?.city || paczkomat.city || '',
        post_code: paczkomat.address?.postCode || paczkomat.postCode || paczkomat.postcode || '',
        street: paczkomat.address?.street || paczkomat.street,
        building_number: paczkomat.address?.buildingNumber || paczkomat.buildingNumber,
        flat_number: paczkomat.address?.flatNumber || paczkomat.flatNumber,
      }
    };
    
    onSelect(formatted);
    handleModalClose();
  };

  const handleChange = () => {
    onSelect(null as any);
  };

  const handleModalClose = () => {
    setOpened(false);
    setSearchQuery('');
    setPaczkomats([]);
    setError(null);
    setSelectedOnMap(null);
    setUserLocation(null);
    setMapCenter(defaultCenter);
  };

  const handleOpenModal = () => {
    setOpened(true);
  };

  return (
    <>
      {selectedPaczkomat ? (
        <Paper p="md" withBorder>
          <Text fw={600} mb="xs">{t('paczkomat.selected')}</Text>
          <Text size="sm" mb="xs">{selectedPaczkomat.name}</Text>
          <Text size="sm" c="dimmed" mb="md">
            {selectedPaczkomat.address.line1}, {selectedPaczkomat.address.post_code} {selectedPaczkomat.address.city}
          </Text>
          <Button variant="light" onClick={handleChange} size="sm">
            {t('paczkomat.change')}
          </Button>
        </Paper>
      ) : (
        <Button
          leftSection={<IconMapPin size={16} />}
          onClick={handleOpenModal}
          fullWidth
        >
          {t('paczkomat.select')}
        </Button>
      )}

      <Modal
        opened={opened}
        onClose={handleModalClose}
        title={t('paczkomat.modalTitle')}
        size="100%"
        centered
        styles={{
          body: {
            padding: '1rem',
          },
        }}
      >
        <Stack gap="md">
          <Group gap="xs">
            <TextInput
              leftSection={<IconSearch size={16} />}
              placeholder={t('paczkomat.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="md"
              style={{ flex: 1 }}
            />
            <Button
              leftSection={<IconCurrentLocation size={16} />}
              onClick={handleFindNearby}
              loading={locationLoading}
              variant="light"
            >
              {t('paczkomat.findNearby')}
            </Button>
          </Group>

          <Text size="xs" c="dimmed">
            {t('paczkomat.searchExamples')}
          </Text>

          {error && (
            <Alert color="orange" title={t('paczkomat.note')}>
              {error}
            </Alert>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', height: '600px' }}>
            {/* Results List */}
            <div style={{ display: 'flex', flexDirection: 'column', height: '600px', overflow: 'hidden' }}>
              {loading && (
                <Group justify="center" py="xl">
                  <Loader size="md" />
                  <Text c="dimmed">{t('paczkomat.searching')}</Text>
                </Group>
              )}

              {!loading && searchQuery.length > 0 && searchQuery.length < 3 && !userLocation && (
                <Text c="dimmed" ta="center" py="xl">
                  {t('paczkomat.typeToSearch')}
                </Text>
              )}

              {!loading && paczkomats.length > 0 && (
                <>
                  <ScrollArea style={{ flex: 1, height: 0 }}>
                    <Stack gap="xs">
                      {paczkomats.map((paczkomat: any) => {
                        const paczkomatId = paczkomat.name || paczkomat.locationName || paczkomat.id || '';
                        const selectedId = selectedOnMap?.name || selectedOnMap?.locationName || selectedOnMap?.id || '';
                        const isSelected = paczkomatId === selectedId;
                        
                        return (
                          <Paper
                            key={paczkomatId || Math.random()}
                            p="sm"
                            withBorder
                            style={{
                              cursor: 'pointer',
                              border: isSelected ? '2px solid #228be6' : '1px solid #dee2e6',
                              backgroundColor: isSelected ? '#e7f5ff' : 'white',
                            }}
                            onClick={() => setSelectedOnMap(paczkomat)}
                          >
                            <Text fw={600} size="sm" mb="xs">
                              {paczkomat.name || paczkomat.locationName || ''}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {paczkomat.address?.line1 || paczkomat.address?.street || paczkomat.addressLine1 || paczkomat.address || ''}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {paczkomat.address_details?.post_code || paczkomat.address?.postCode || paczkomat.postCode || paczkomat.postcode || ''} {paczkomat.address_details?.city || paczkomat.address?.city || paczkomat.city || ''}
                            </Text>
                            {(paczkomat.location_description || paczkomat.locationDescription) && (
                              <Text size="xs" c="dimmed" mt="xs">
                                📍 {paczkomat.location_description || paczkomat.locationDescription}
                              </Text>
                            )}
                            {(paczkomat.opening_hours || paczkomat.openingHours) && (
                              <Text size="xs" c="blue" mt="xs">
                                🕐 {paczkomat.opening_hours || paczkomat.openingHours}
                              </Text>
                            )}
                          </Paper>
                        );
                      })}
                    </Stack>
                  </ScrollArea>
                  
                  <Text size="xs" c="dimmed" ta="center" mt="sm" style={{ flexShrink: 0 }}>
                    {userLocation 
                      ? t('paczkomat.foundNearby', { count: paczkomats.length.toString() })
                      : t('paczkomat.found', { count: paczkomats.length.toString() })
                    }
                  </Text>
                </>
              )}

              {!loading && searchQuery.length >= 3 && paczkomats.length === 0 && !userLocation && (
                <Text c="dimmed" ta="center" py="xl">
                  {t('paczkomat.noResults')}
                </Text>
              )}

              {!loading && !searchQuery && !userLocation && paczkomats.length === 0 && (
                <Text c="dimmed" ta="center" py="xl">
                  {t('paczkomat.searchHint')}
                </Text>
              )}
            </div>

            {/* Map */}
            {opened && (
              <div 
                ref={mapContainerRef}
                style={{ 
                  height: '600px', 
                  width: '100%', 
                  borderRadius: '8px', 
                  overflow: 'hidden',
                  backgroundColor: '#f5f5f5'
                }}
              />
            )}
          </div>

          {selectedOnMap && (
            <Button 
              onClick={() => handleSelect(selectedOnMap)} 
              fullWidth 
              size="lg"
              color="blue"
            >
              {t('paczkomat.confirmSelection')} {selectedOnMap.name || selectedOnMap.locationName}
            </Button>
          )}
        </Stack>
      </Modal>
    </>
  );
};
